import React from 'react';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import { MobileActionBar } from 'src/modules/shared/components/molecules/mobile-action-bar/mobile-action-bar';
import { useI18n } from 'src/modules/shared/hooks/provider-bounded/i18n';

/** Props for the phone action bar. */
interface WebsiteMobileActionBarProps {
  /** Whether saving the draft is unavailable right now. */
  saveDisabled: boolean;
  /** Whether the draft is being saved. */
  saveLoading: boolean;
  /** Whether publishing is unavailable right now. */
  publishDisabled: boolean;
  /** Whether a save or publish is running. */
  publishLoading: boolean;
  /** Opens the full-screen preview. */
  onOpenPreview: () => void;
  /** Saves the draft without publishing. */
  onSave: () => void;
  /** Saves and publishes the draft. */
  onPublish: () => void;
}

/** Equal-width bottom-bar button with a comfortable touch height. */
const ACTION_CLASS_NAME = 'h-11 min-w-0 flex-1 px-2';

/**
 * The header's three actions in the shared phone action bar, in the same
 * order: preview, save draft and publish.
 *
 * @param props - Save and publish state, and the actions.
 * @returns The fixed bottom bar.
 */
export const WebsiteMobileActionBar: React.FC<WebsiteMobileActionBarProps> = ({
  saveDisabled,
  saveLoading,
  publishDisabled,
  publishLoading,
  onOpenPreview,
  onSave,
  onPublish,
}) => {
  const { t } = useI18n();

  return (
    <MobileActionBar>
      <Button
        className={ACTION_CLASS_NAME}
        variant="secondary"
        appearance="outline"
        onClick={onOpenPreview}
      >
        {t('websites.settings.actions.openPreviewSheet')}
      </Button>
      <Button
        className={ACTION_CLASS_NAME}
        variant="secondary"
        appearance="outline"
        onClick={onSave}
        disabled={saveDisabled}
        loading={saveLoading}
      >
        {t('websites.settings.actions.saveDraftShort')}
      </Button>
      <Button
        className={ACTION_CLASS_NAME}
        onClick={onPublish}
        disabled={publishDisabled}
        loading={publishLoading}
      >
        {t('websites.settings.actions.publishShort')}
      </Button>
    </MobileActionBar>
  );
};
